import React, { useState, useEffect } from "react";

import { Course, StudentResponse } from "../../../models/entities/twygo";
import api from "../../../services/api";

import PageContainer from "../../../containers/PageContainer";

import {
  Row,
  Col,
  Input,
  Divider,
  Tag,
  Badge,
  Typography,
  Collapse,
  Tooltip,
  message,
  Spin,
  Empty,
} from "antd";
import {
  Container,
  Header,
  Content,
  CourseCard,
  CourseName,
  CourseInfo,
  SearchContainer,
  StudentsHeader,
  CourseTitle,
  StudentsCount,
  StudentsList,
  Table,
  Progress,
  CourseDescription,
  Button,
  ActivityItem,
  ActivityContainer,
  BoxPanel,
  ArrowLeft,
  Search,
  PlayCircle,
  FileText,
  Form,
  Select,
  HeaderList,
  ContainerList,
} from "./styles";

const { Panel } = Collapse;
const { Text } = Typography;

const TwygoCourses: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [courses, setCourses] = useState<Course[]>([]);
  const [filteredCourses, setFilteredCourses] = useState<Course[]>([]);
  const [courseTypeFilter, setCourseTypeFilter] = useState<string | null>(null);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [studentsData, setStudentsData] = useState<StudentResponse | null>(
    null
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [sortedInfo, setSortedInfo] = useState<any>({});

  const getCompletedStudentsCount = () => {
    if (!studentsData?.alunos || !Array.isArray(studentsData.alunos)) {
      return 0;
    }
    return studentsData.alunos.filter((aluno) => aluno.progress === 100).length;
  };

  const fetchCourses = async () => {
    setLoading(true);
    try {
      const response = await api.get("/twygo/v2/courses");
      const coursesData = response.data.data.contents;
      const validCoursesData = Array.isArray(coursesData) ? coursesData : [];

      setCourses(validCoursesData);
      setFilteredCourses(validCoursesData);
    } catch (error) {
      console.error("Erro ao carregar cursos:", error);
      message.error(
        "Não foi possível carregar os cursos. Por favor, tente novamente."
      );
      setCourses([]);
      setFilteredCourses([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourses();
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    filterCourses();
    // eslint-disable-next-line 
  }, [search, courseTypeFilter, courses]);

  const filterCourses = () => {
    let filtered = [...courses];

    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(
        (course) =>
          decodeHtmlEntities(course.name || "")
            .toLowerCase()
            .includes(searchLower) ||
          (course.description &&
            decodeHtmlEntities(sanitizeHtml(course.description))
              .toLowerCase()
              .includes(searchLower))
      );
    }

    if (courseTypeFilter) {
      filtered = filtered.filter(
        (course) => course.course_type === courseTypeFilter
      );
    }

    setFilteredCourses(filtered);
  };

  const handleViewCourseUsers = (courseId: number) => {
    const course = courses.find((c) => c.content_id === courseId);
    if (course) {
      setSelectedCourse(course);
      setCurrentPage(1);
      loadStudentsData(courseId);
    }
  };

  const loadStudentsData = async (courseId: number) => {
    setLoadingStudents(true);

    try {
      const response = await api.get(
        `/twygo/v2/info-courses?page=1&per_page=9999&content_id=${courseId}`
      );
      setStudentsData(response.data);
    } catch (error) {
      console.error("Erro ao carregar dados dos alunos:", error);
      message.error(
        "Não foi possível carregar os dados dos alunos. Por favor, tente novamente."
      );
    } finally {
      setLoadingStudents(false);
    }
  };

  const handleTableChange = (pagination: any, _filters: any, sorter: any) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
    setSortedInfo(sorter);
  };

  const handleBackToCourses = () => {
    setSelectedCourse(null);
    setStudentsData(null);
    setSortedInfo({});
  };

  const getCourseTypeOptions = () => {
    if (!Array.isArray(courses)) {
      return [];
    }

    const types = Array.from(
      new Set(
        courses
          .filter((course) => course && typeof course.course_type === "string")
          .map((course) => course.course_type as string)
      )
    );

    return types.map((type) => ({ label: type, value: type }));
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("pt-BR");
  };

  const getModalityText = (modality: string) => {
    switch (modality) {
      case "onlineRecorded":
        return "Online (Gravado)";
      case "onlineLive":
        return "Online (Ao vivo)";
      case "hybrid":
        return "Híbrido";
      case "face2face":
        return "Presencial";
      default:
        return modality;
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case "video":
        return <PlayCircle />;
      case "pdf":
        return <FileText />;
      case "questions":
        return <Form />;
      default:
        return null;
    }
  };

  const sanitizeHtml = (html: string) => {
    if (!html) return "";
    return html.replace(/<[^>]*>/g, "");
  };

  const decodeHtmlEntities = (text: string) => {
    if (!text) return "";
    const textArea = document.createElement("textarea");
    textArea.innerHTML = text;
    return textArea.value;
  };

  const truncateText = (text: string, maxLength: number = 120) => {
    if (!text) return "";
    const sanitized = sanitizeHtml(text);
    const decoded = decodeHtmlEntities(sanitized);
    if (decoded.length <= maxLength) return decoded;
    return decoded.substring(0, maxLength) + "...";
  };

  const getStatusInfo = (status: string) => {
    switch (status) {
      case "pending":
        return { color: "warning", text: "Pendente" };
      case "confirmed":
        return { color: "success", text: "Confirmado" };
      case "rejected":
        return { color: "error", text: "Rejeitado" };
      case "reversed":
        return { color: "default", text: "Revertido" };
      case "payment_overdue":
        return { color: "error", text: "Pagamento Atrasado" };
      case "refund_failed":
        return { color: "error", text: "Reembolso Falhou" };
      case "refund_solicitation":
        return { color: "processing", text: "Solicitação de Reembolso" };
      case "refund_sent":
        return { color: "blue", text: "Reembolso Enviado" };
      case "canceled":
        return { color: "error", text: "Cancelado" };
      default:
        return { color: "default", text: status || "Desconhecido" };
    }
  };

  const studentColumns = [
    {
      title: "Nome",
      dataIndex: "nome",
      key: "nome",
      ellipsis: true,
      sorter: (a, b) => a.nome.localeCompare(b.nome),
      sortOrder: sortedInfo.columnKey === "nome" && sortedInfo.order,
      render: (nome: string) => (
        <Tooltip title={nome}>
          <span>{nome}</span>
        </Tooltip>
      ),
      showSorterTooltip: { title: "Clique para ordenar por nome" },
    },
    {
      title: "Cargo",
      dataIndex: "role",
      key: "role",
      sorter: (a, b) => {
        if (!a.role && !b.role) return 0;
        if (!a.role) return 1;
        if (!b.role) return -1;
        return a.role.localeCompare(b.role);
      },
      sortOrder: sortedInfo.columnKey === "role" && sortedInfo.order,
      render: (role: string) => (
        <span>{role ? role : "Sem cargo definido"}</span>
      ),
      showSorterTooltip: { title: "Clique para ordenar por cargo" },
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      ellipsis: true,
      width: "15%",
      sorter: (a, b) => a.email.localeCompare(b.email),
      sortOrder: sortedInfo.columnKey === "email" && sortedInfo.order,
      render: (email: string) => (
        <Tooltip title={email}>
          <span>{email}</span>
        </Tooltip>
      ),
      showSorterTooltip: { title: "Clique para ordenar por email" },
    },
    {
      title: "Progresso",
      dataIndex: "progress",
      key: "progress",
      width: "30%",
      sorter: (a, b) => (a.progress || 0) - (b.progress || 0),
      sortOrder: sortedInfo.columnKey === "progress" && sortedInfo.order,
      render: (progress: number) => (
        <Progress
          percent={progress !== null && progress !== undefined ? progress : 0}
        />
      ),
      showSorterTooltip: { title: "Clique para ordenar por progresso" },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      sorter: (a, b) => {
        const aStatus = a.status ? a.status.toLowerCase() : "";
        const bStatus = b.status ? b.status.toLowerCase() : "";
        return aStatus.localeCompare(bStatus);
      },
      sortOrder: sortedInfo.columnKey === "status" && sortedInfo.order,
      render: (status: string) => {
        const statusInfo = getStatusInfo(status.toLowerCase());
        return (
          <Badge status={statusInfo.color as any} text={statusInfo.text} />
        );
      },
      showSorterTooltip: { title: "Clique para ordenar por status" },
    },
    {
      title: "Nota",
      dataIndex: "score",
      key: "score",
      width: "5%",
      sorter: (a, b) => (a.score || 0) - (b.score || 0),
      sortOrder: sortedInfo.columnKey === "score" && sortedInfo.order,
      showSorterTooltip: { title: "Clique para ordenar por nota" },
    },
    {
      title: "Completado em",
      dataIndex: "completed_at",
      key: "completed_at",
      width: "10%",
      sorter: (a, b) => {
        if (!a.completed_at && !b.completed_at) return 0;
        if (!a.completed_at) return 1;
        if (!b.completed_at) return -1;
        return (
          new Date(a.completed_at).getTime() -
          new Date(b.completed_at).getTime()
        );
      },
      sortOrder: sortedInfo.columnKey === "completed_at" && sortedInfo.order,
      render: (date: string | null) => formatDate(date),
      showSorterTooltip: { title: "Clique para ordenar por data de conclusão" },
    },
  ];

  return (
    <PageContainer route="Gerenciamento de Cursos">
      <Container>
        {!selectedCourse ? (
          <>
            <div>
              <SearchContainer>
                <Input
                  placeholder="Buscar cursos..."
                  prefix={<Search />}
                  allowClear
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ width: 300 }}
                />

                <Select
                  placeholder="Filtrar por curso"
                  allowClear
                  style={{ width: 200 }}
                  options={getCourseTypeOptions()}
                  onChange={(value) =>
                    setCourseTypeFilter(value as string | null)
                  }
                />
              </SearchContainer>
            </div>

            <Divider />

            <Content>
              {loading ? (
                <div style={{ textAlign: "center", padding: "40px 0" }}>
                  <Spin size="default" tip="Carregando cursos..." />
                </div>
              ) : courses.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px 0" }}>
                  <Empty
                    description="Nenhum curso disponível no momento"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                </div>
              ) : filteredCourses.length === 0 ? (
                <div style={{ textAlign: "center", margin: "40px 0" }}>
                  <Empty
                    description="Nenhum curso encontrado com os filtros aplicados"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                </div>
              ) : (
                <Row gutter={[16, 16]}>
                  {filteredCourses.map((course) => (
                    <Col xs={24} sm={12} md={8} key={course.content_id}>
                      <CourseCard>
                        <Header>
                          <CourseName>
                            {decodeHtmlEntities(course.name)}
                          </CourseName>
                          <Button
                            onClick={() =>
                              handleViewCourseUsers(course.content_id)
                            }
                          >
                            Ver Alunos
                          </Button>
                        </Header>

                        <Tooltip
                          title={
                            course.description
                              ? decodeHtmlEntities(
                                  sanitizeHtml(course.description)
                                )
                              : ""
                          }
                          placement="top"
                        >
                          <CourseDescription>
                            {truncateText(course.description || "", 120)}
                          </CourseDescription>
                        </Tooltip>

                        <CourseInfo>
                          <div>
                            <Tag color="blue">{course.course_type}</Tag>
                            <Tag color="purple">
                              {getModalityText(course.modality || "")}
                            </Tag>
                          </div>
                        </CourseInfo>

                        <Divider style={{ margin: "5px 0" }} />

                        <BoxPanel>
                          <Collapse
                            ghost
                            bordered={false}
                            expandIconPosition="left"
                          >
                            <Panel
                              header={
                                <Text style={{ color: "var(--primary)" }}>
                                  Ver atividades do curso
                                </Text>
                              }
                              key={`course-${course.content_id}`}
                            >
                              <div
                                style={{
                                  maxHeight: "55px",
                                  overflowY: "auto",
                                  padding: "0",
                                }}
                              >
                                {(() => {
                                  const activities = course.activities;
                                  if (!activities || activities.length === 0) {
                                    return (
                                      <Text type="secondary">
                                        Nenhum curso disponível
                                      </Text>
                                    );
                                  }
                                  const sortedActivities = [...activities].sort(
                                    (a, b) => a.sequence - b.sequence
                                  );
                                  return (
                                    <ActivityContainer>
                                      {sortedActivities.map((item, index) => (
                                        <ActivityItem key={index}>
                                          {getActivityIcon(item.activity_type)}
                                          <span>
                                            {item.title ||
                                              `Atividade ${item.sequence + 1}`}
                                          </span>
                                        </ActivityItem>
                                      ))}
                                    </ActivityContainer>
                                  );
                                })()}
                              </div>
                            </Panel>
                          </Collapse>
                        </BoxPanel>
                      </CourseCard>
                    </Col>
                  ))}
                </Row>
              )}
            </Content>
          </>
        ) : (
          <ContainerList>
            <HeaderList>
              <StudentsHeader>
                <Tooltip
                  title={
                    loadingStudents
                      ? "Carregando dados dos alunos..."
                      : "Voltar para a lista de cursos"
                  }
                >
                  <ArrowLeft
                    disabled={loadingStudents}
                    onClick={loadingStudents ? undefined : handleBackToCourses}
                    style={
                      loadingStudents
                        ? { opacity: 0.5, cursor: "not-allowed" }
                        : undefined
                    }
                  />
                </Tooltip>
                <CourseTitle>
                  Alunos do curso:{" "}
                  {selectedCourse && decodeHtmlEntities(selectedCourse.name)}
                </CourseTitle>
              </StudentsHeader>
            </HeaderList>

            <Divider style={{ margin: "5px 0" }} />

            <StudentsCount>
              Total de alunos inscritos:{" "}
              <strong>{studentsData?.total_inscritos || 0}</strong>
              {" | "}
              Alunos que concluíram 100%:{" "}
              <strong>{getCompletedStudentsCount()}</strong>
            </StudentsCount>
            <Content>
              <StudentsList>
                <Table
                  dataSource={studentsData?.alunos || []}
                  columns={studentColumns}
                  rowKey="user_id"
                  loading={loadingStudents}
                  scroll={{ y: 550 }}
                  pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    total: studentsData?.total_inscritos || 0,
                    showSizeChanger: true,
                  }}
                  onChange={handleTableChange}
                  locale={{
                    emptyText: (
                      <Empty
                        description="Nenhum aluno encontrado para este curso"
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                      />
                    ),
                  }}
                />
              </StudentsList>
            </Content>
          </ContainerList>
        )}
      </Container>
    </PageContainer>
  );
};

export default TwygoCourses;
